import { useEffect, useRef } from 'react';

import { useSidebar } from '@/components/ui/sidebar';

interface SidebarAutoCollapseProps {
    /** True while a route that carries its own nav pane is open. */
    shouldCollapse: boolean;
}

/**
 * Collapses the app sidebar to its icon rail while `shouldCollapse` is true, and
 * hands the previous state back when it flips false. Renders nothing — it must
 * sit inside `SidebarProvider`, which is why the effect can't live in AppLayout.
 *
 * Manual control still wins: the header trigger, the rail and ⌘B all work while
 * the route is open, and the sidebar is only re-expanded on exit if it is still
 * in the collapsed state this component put it in.
 */
function SidebarAutoCollapse({ shouldCollapse }: SidebarAutoCollapseProps) {
    const { isMobile, open, setOpen } = useSidebar();

    // `setOpen`'s identity changes with `open`, and `open` changes on every
    // toggle — reading both through a ref keeps the transition effect below
    // firing on route changes only, never on toggles (which would otherwise
    // re-collapse the sidebar right after the user opened it).
    const sidebar = useRef({ open, setOpen });
    useEffect(() => {
        sidebar.current = { open, setOpen };
    });

    // Whether this component is the one that collapsed the sidebar.
    const didCollapse = useRef(false);

    useEffect(() => {
        // Mobile renders the sidebar as an overlay sheet — no width to reclaim.
        if (isMobile) return;

        const { open: isOpen, setOpen: apply } = sidebar.current;

        if (shouldCollapse) {
            if (!isOpen) return; // already collapsed — the user's own choice
            didCollapse.current = true;
            apply(false);
            return;
        }

        // Leaving: restore only if the sidebar is still collapsed, i.e. the user
        // has not re-opened it in the meantime.
        if (didCollapse.current && !isOpen) apply(true);
        didCollapse.current = false;
    }, [shouldCollapse, isMobile]);

    return null;
}

export { SidebarAutoCollapse };
